import React, { useState, useEffect } from "react";
import { getFirestore, collection, addDoc, doc, updateDoc, increment, getDoc, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import MultipleChoicePSQuestion from "./MultipleChoicePSQuestion";
import MultipleChoiceChartPSQuestion from "./MultipleChoiceChartPSQuestion";
import TrueFalsePSQuestion from "./TrueFalsePSQuestion";
import TrueFalseChartPSQuestion from "./TrueFalseChartPSQuestion";
import MatchTheFollowingPSQuestion from "./MatchTheFollowingPSQuestion";
import MatchTheFollowingChartPSQuestion from "./MatchTheFollowingChartPSQuestion";
import { getPracticeSessionQuestions } from "../datafetch/PracticeSessionService";

const PracticeSessionQuestions = ({ lessonId, topicId, onComplete }) => {
  const [questions, setQuestions] = useState([]); // Store all questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [animateLogo, setAnimateLogo] = useState(false);

  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    const fetchedQuestions = await getPracticeSessionQuestions(lessonId);
    
    console.log(`✅ Fetched ${fetchedQuestions.length} questions for lessonId: ${lessonId}`);

    if (fetchedQuestions.length > 0) {
      setQuestions(fetchedQuestions);
      setIsDataLoaded(true);
    } else {
      onComplete(); // No questions, go to FinalQuotes
    }
  };

  // ✅ Move to the next question or complete the session
  const handleNextQuestion = async () => {
    console.log(`✅ Moving to next question. Current index: ${currentQuestionIndex}`);

    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
      console.log(`✅ Updated question index: ${currentQuestionIndex + 1}`);
    } else {
      //console.log("✅ No more questions, finishing practice session.");
      await completePracticeSession();
      onComplete(); // Move to FinalQuotes when all questions are done
    }
  };

  // ✅ Function to handle Firestore updates after session completion
  const completePracticeSession = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn("⚠️ No user logged in, skipping Firestore updates.");
        return;
      }
  
      const userId = user.uid;
      const sessionId = `practice-${new Date().toISOString()}-${Math.floor(Math.random() * 1000)}`;
  
      // ✅ Insert into userpracticesessions
      await addDoc(collection(db, "userpracticesessions"), {
        date: new Date(),
        lessonid: lessonId,
        userid: userId,
        channel: "1",  //   empty / no value  represents ios, 1 represents web, 2 represents android
        sessiontype:"practice",
        challengeid: topicId || "N/A",
      });
      //console.log("✅ Logged practice session.");
  
      // ✅ Update usertotaltokens (increment by 10)
      const totalTokensRef = doc(db, "usertotaltokens", userId);
      await updateDoc(totalTokensRef, {
        date: new Date(),
        totaltokens: increment(10),
      });
      //console.log("✅ Incremented total tokens by 10.");
  
      // ✅ Update userstreaktotal only if it's a new streak day
      const userStreakRef = doc(db, "userstreaktotal", userId);
      const userStreakDoc = await getDoc(userStreakRef);
  
      const today = new Date();
  
      if (userStreakDoc.exists()) {
        const userData = userStreakDoc.data();
        const lastStreakUpdateDate = userData.date?.toDate() || new Date(0);
        const totalStreakCount = userData.totalstreakcount || 0;
  
        console.log(`ℹ️ Last Streak Update Date: ${lastStreakUpdateDate.toDateString()}`);
        console.log(`ℹ️ Total Streak Count: ${totalStreakCount}`);
  
        // ✅ If today is NOT the same as the last update, increment streak count
        if (!isSameDay(today, lastStreakUpdateDate) || totalStreakCount === 0) {
          await updateDoc(userStreakRef, {
            date: new Date(),
            totalstreakcount: increment(1),
          });
          //console.log("✅ Streak incremented.");
        } else {
          //console.log("ℹ️ Streak already updated today.");
        }
      } else {
        // ✅ If no streak entry exists, create one
        await setDoc(userStreakRef, {
          date: new Date(),
          totalstreakcount: 1,
          userid: userId,
        });
        //console.log("✅ Created new streak entry.");
      }
    } catch (error) {
      console.error("❌ Error completing practice session:", error);
    }
  };
  
  // ✅ Helper function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return date1.toDateString() === date2.toDateString();
  };
  

  // ✅ Define `currentQuestion` before JSX
  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div style={{ padding: "20px", backgroundColor: "black", color: "white", minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
      
      {isDataLoaded ? (
        <>
          {questions.length > 0 && currentQuestionIndex < questions.length && (
            <>
              {/* Render the appropriate question component based on question type */}
              {["1", "4"].includes(currentQuestion.questiontypeid) && (
                <MultipleChoicePSQuestion question={currentQuestion} onNext={handleNextQuestion} />
              )}
              {["5.1", "5.4", "6.1", "6.4", "7.1", "7.4", "6-2.1", "6-2.4"].includes(currentQuestion.questiontypeid) && (
                <MultipleChoiceChartPSQuestion question={currentQuestion} onNext={handleNextQuestion} />
              )}
              {currentQuestion.questiontypeid === "2" && (
                <TrueFalsePSQuestion question={currentQuestion} onNext={handleNextQuestion} />
              )}
              {["5.2", "6.2", "7.2", "6-2.2"].includes(currentQuestion.questiontypeid) && (
                <TrueFalseChartPSQuestion question={currentQuestion} onNext={handleNextQuestion} />
              )}
              {currentQuestion.questiontypeid === "3" && (
                <MatchTheFollowingPSQuestion question={currentQuestion} onNext={handleNextQuestion} />
              )}
              {["5.3", "6.3", "6-2.3", "7.3"].includes(currentQuestion.questiontypeid) && (
                <MatchTheFollowingChartPSQuestion question={currentQuestion} onNext={handleNextQuestion} />
              )}
            </>
          )}
        </>
      ) : (
        // Loading State
        <div style={{ textAlign: "center" }}>
          <img
            src="/images/fintorro-logo-whitebackground.png"
            alt="Fintorro Logo"
            style={{
              width: "100px",
              height: "100px",
              animation: animateLogo ? "bounce 0.5s infinite alternate" : "none",
            }}
            onLoad={() => setAnimateLogo(true)}
          />
          <p style={{ marginTop: "10px", fontSize: "16px", color: "white" }}>
            Hang tight, personalizing practice session...
          </p>
        </div>
      )}
    </div>
  );
};

export default PracticeSessionQuestions;
